// ===========================================================================
// Is there a person in front of this camera?
//
// "Is there a way to only show the camera if there's an actual person in
// both?" (the operator, 2026-09-15). A camera can be covered, or pointed at a
// ceiling, and still count as on. So a camera is checked once, at the moment
// it would open: in a 1:1, until both cameras open; in a room, when somebody
// turns theirs on. Never after. Somebody who leaves the room for a moment, or
// falls asleep off-centre, keeps their camera.
//
// The check is the selfie-segmentation model Lumi already serves for
// background blur (selfie-segmenter.js), run in the browser: no picture leaves
// the device for it. The model marks which pixels are a person; a person at a
// normal distance covers far more than MIN_PERSON_COVERAGE of the picture, and
// a covered lens or an empty ceiling covers next to none.
//
// It stops a covered or pointed-away camera. It is not a security check: a
// browser that cannot run the model answers yes, because the point is to catch
// a mistake, not to lock out an old phone. Moderation still samples frames
// once cameras are open. ASCII only.
// ===========================================================================

/** The share of the picture that has to be a person. */
export const MIN_PERSON_COVERAGE = 0.04;
/** The size the model's answer is read at. */
export const SAMPLE_W = 64;
export const SAMPLE_H = 36;

/** The share of pixels the mask marks as a person (alpha at or above half). */
export function maskCoverage(rgba) {
  if (!rgba || rgba.length < 4) return 0;
  let on = 0;
  let n = 0;
  for (let i = 3; i < rgba.length; i += 4) {
    n += 1;
    if (rgba[i] >= 128) on += 1;
  }
  return n ? on / n : 0;
}

/**
 * One look: does this video show a person?
 *
 * @param {{ video: HTMLVideoElement, segmenter: Function|null, doc?: Document }} p
 *   segmenter is selfie-segmenter.js's (video, w, h) -> mask canvas, or null
 *   when the model cannot run here
 * @returns {Promise<boolean>}
 */
export async function seesPerson({ video, segmenter, doc = globalThis.document }) {
  if (!video || !video.videoWidth) return false; // no picture yet is not a person
  if (!segmenter) return true; // cannot check: never lock anybody out for it
  try {
    const mask = await segmenter(video, SAMPLE_W, SAMPLE_H);
    if (!mask) return true;
    const c = doc.createElement('canvas');
    c.width = SAMPLE_W;
    c.height = SAMPLE_H;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(mask, 0, 0, SAMPLE_W, SAMPLE_H);
    return maskCoverage(ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data) >= MIN_PERSON_COVERAGE;
  } catch {
    return true;
  }
}

/**
 * A hidden video element playing one camera track, so the camera can be
 * checked while the self-view is off the screen (the text layout hides it).
 * Kept in the page as a transparent pixel (.probe-video) because a video
 * outside the page may never draw a frame on some phones.
 */
export function createProbeVideo(track, doc = globalThis.document) {
  const video = doc.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.className = 'probe-video';
  video.srcObject = new MediaStream([track]);
  doc.body.append(video);
  video.play?.().catch(() => {});
  return {
    video,
    stop() {
      video.srcObject = null;
      video.remove();
    },
  };
}

/**
 * Look for a person a few times, a moment apart, and answer yes as soon as one
 * is seen. For a room camera being turned on: somebody still settling in front
 * of it gets a couple of seconds, not a single frame.
 *
 * @param {{ track: MediaStreamTrack|null, getSegmenter: () => Promise<Function|null>,
 *           tries?: number, gapMs?: number, doc?: Document }} p
 */
export async function lookForPerson({ track, getSegmenter, tries = 3, gapMs = 700, doc = globalThis.document }) {
  if (!track || track.readyState !== 'live' || track.enabled === false) return false;
  const segmenter = await getSegmenter().catch(() => null);
  if (!segmenter) return true;
  const probe = createProbeVideo(track, doc);
  try {
    for (let wait = 0; wait < 30 && !probe.video.videoWidth; wait += 1) await delay(50);
    for (let i = 0; i < tries; i += 1) {
      if (await seesPerson({ video: probe.video, segmenter, doc })) return true;
      if (i < tries - 1) await delay(gapMs);
    }
    return false;
  } finally {
    probe.stop();
  }
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default { MIN_PERSON_COVERAGE, SAMPLE_W, SAMPLE_H, maskCoverage, seesPerson, createProbeVideo, lookForPerson };
